import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
  FolderOpen, Upload, Trash2, FileText, Cloud, Link2, Unlink, Loader2,
  ChevronRight, X, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);

function OneDrivePicker({ api, onClose, onLinked }) {
  const [folders, setFolders] = useState([]);
  const [stack, setStack] = useState([]); // [{id, name}]
  const [loading, setLoading] = useState(true);

  const browse = async (parentId) => {
    setLoading(true);
    try {
      const r = await api.get(`/onedrive/folders${parentId ? `?parent_id=${parentId}` : ""}`);
      setFolders(r.data?.folders || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur OneDrive"); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { browse(null); }, []);

  const enter = (f) => { setStack([...stack, f]); browse(f.id); };
  const back = () => { const s = stack.slice(0, -1); setStack(s); browse(s.length ? s[s.length - 1].id : null); };
  const current = stack[stack.length - 1];

  const link = async (folder) => {
    try {
      await api.put("/ai/folder/onedrive", { folder_id: folder.id, folder_name: folder.name });
      toast.success(`Dossier « ${folder.name} » lié`);
      onLinked(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="onedrive-picker-dialog">
      <div className="bg-[#0f0a18] border border-white/10 rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h3 className="font-bold text-white text-sm flex items-center gap-2"><Cloud className="w-4 h-4 text-sky-400" /> Choisir un dossier OneDrive</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-slate-400" data-testid="picker-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-2 flex items-center gap-2 text-xs text-slate-400 border-b border-white/5">
          {stack.length > 0 && <button onClick={back} className="text-[#f7c935] hover:underline" data-testid="picker-back">← Retour</button>}
          <span className="truncate">/{stack.map((s) => s.name).join("/")}</span>
          {current && (
            <Button onClick={() => link(current)} className="ml-auto bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-7 text-[11px]" data-testid="link-current-folder">
              <Link2 className="w-3 h-3 mr-1" /> Lier ce dossier
            </Button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-sky-400" /></div>
          ) : folders.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">Aucun sous-dossier</p>
          ) : folders.map((f) => (
            <div key={f.id} className="flex items-center justify-between px-5 py-2.5 hover:bg-white/[0.03] border-b border-white/5">
              <button onClick={() => enter(f)} className="flex items-center gap-2 text-sm text-white min-w-0" data-testid={`picker-folder-${f.id}`}>
                <FolderOpen className="w-4 h-4 text-sky-400 flex-shrink-0" />
                <span className="truncate">{f.name}</span>
                <span className="text-[10px] text-slate-500">({f.child_count})</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
              </button>
              <Button onClick={() => link(f)} variant="outline" className="border-white/10 text-slate-300 h-7 text-[11px]" data-testid={`link-folder-${f.id}`}>
                Lier
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const SocieteFolderPanel = ({ api, societeId }) => {
  const [docs, setDocs] = useState([]);
  const [oneDriveLink, setOneDriveLink] = useState(null);
  const [odStatus, setOdStatus] = useState(null);
  const [odFiles, setOdFiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/ai/folder");
      setDocs(r.data?.docs || []);
      setOneDriveLink(r.data?.onedrive || null);
    } catch { /* ignore */ }
    try {
      const s = await api.get("/onedrive/status");
      setOdStatus(s.data);
    } catch { setOdStatus({ connected: false }); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); setOdFiles(null); load(); }, [societeId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 8 Mo)"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/ai/folder/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`« ${file.name} » ajouté au dossier société`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'upload"); }
    setUploading(false);
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`Retirer « ${d.name} » du dossier société ?`)) return;
    try { await api.delete(`/ai/folder/docs/${d.doc_id}`); load(); }
    catch { toast.error("Erreur"); }
  };

  const unlink = async () => {
    if (!window.confirm("Délier le dossier OneDrive ?")) return;
    try { await api.delete("/ai/folder/onedrive"); setOdFiles(null); load(); }
    catch { toast.error("Erreur"); }
  };

  const browseOdFiles = async () => {
    try {
      const r = await api.get("/ai/folder/onedrive/files");
      setOdFiles(r.data?.files || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur OneDrive"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="folder-panel">
      {/* Documents internes */}
      <div className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
        <div className="flex items-center justify-between mb-3">
          <p className="font-bold text-sm flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
            <FileText className="w-4 h-4 text-[#f7c935]" /> Documents internes ({docs.length})
          </p>
          <label className={`cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-doc-label">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Ajouter
            <input type="file" className="hidden" onChange={upload} accept=".pdf,.docx,.txt,.md,.csv,.json,.html" data-testid="upload-doc-input" />
          </label>
        </div>
        <p className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>
          PDF, Word, texte… Le contenu est lu par vos agents IA (Agent Administratif et orchestrateur) pour répondre avec le contexte de la société.
        </p>
        {docs.length === 0 ? (
          <p className="text-xs text-center py-8" style={{ color: "var(--text-dimmed)" }}>Aucun document — ajoutez vos statuts, contrats, assurances…</p>
        ) : (
          <div className="divide-y divide-white/5">
            {docs.map((d) => (
              <div key={d.doc_id} className="flex items-center gap-2 py-2" data-testid={`doc-row-${d.doc_id}`}>
                <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs truncate" style={{ color: "var(--text-primary)" }} data-testid={`doc-name-${d.doc_id}`}>{d.name}</p>
                  <p className="text-[10px]" style={{ color: "var(--text-dimmed)" }}>
                    {fmtSize(d.size)} · {new Date(d.created_at).toLocaleDateString("fr-FR")}{d.has_text ? " · lisible par l'IA" : " · contenu non textuel"}
                  </p>
                </div>
                <button onClick={() => removeDoc(d)} className="p-1 rounded hover:bg-red-500/10 text-red-400" data-testid={`delete-doc-${d.doc_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* OneDrive */}
      <div className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
        <p className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: "var(--text-primary)" }}>
          <Cloud className="w-4 h-4 text-sky-400" /> Pont OneDrive
        </p>
        {!odStatus?.connected ? (
          <div className="text-center py-8">
            <Cloud className="w-10 h-10 mx-auto mb-2 text-sky-400/30" />
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>OneDrive n'est pas connecté.</p>
            <p className="text-[11px] mt-1" style={{ color: "var(--text-dimmed)" }}>Connectez-le dans Paramètres → Intégrations OneDrive, puis liez un dossier ici.</p>
          </div>
        ) : oneDriveLink ? (
          <div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-sky-500/5 border border-sky-500/20 mb-3" data-testid="linked-folder-box">
              <div className="flex items-center gap-2 min-w-0">
                <FolderOpen className="w-4 h-4 text-sky-400 flex-shrink-0" />
                <p className="text-xs text-white truncate">{oneDriveLink.folder_name}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={browseOdFiles} className="p-1.5 rounded hover:bg-white/10 text-slate-400" title="Voir les fichiers" data-testid="browse-od-files">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                <button onClick={unlink} className="p-1.5 rounded hover:bg-red-500/10 text-red-400" title="Délier" data-testid="unlink-od-folder">
                  <Unlink className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <p className="text-[11px] mb-2" style={{ color: "var(--text-muted)" }}>
              Les agents lisent la liste ET le contenu des fichiers lisibles (PDF, Word, texte…) de ce dossier.
            </p>
            {odFiles === null ? (
              <button onClick={browseOdFiles} className="text-xs text-sky-400 hover:underline" data-testid="load-od-files">Afficher les fichiers du dossier →</button>
            ) : odFiles.length === 0 ? (
              <p className="text-xs" style={{ color: "var(--text-dimmed)" }}>Dossier vide</p>
            ) : (
              <div className="divide-y divide-white/5 max-h-64 overflow-y-auto" data-testid="od-files-list">
                {odFiles.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 py-1.5">
                    {f.is_folder ? <FolderOpen className="w-3.5 h-3.5 text-sky-400" /> : <FileText className="w-3.5 h-3.5 text-slate-500" />}
                    <p className="text-xs flex-1 truncate" style={{ color: "var(--text-primary)" }}>{f.name}</p>
                    {!f.is_folder && <span className="text-[10px]" style={{ color: "var(--text-dimmed)" }}>{fmtSize(f.size)}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>OneDrive connecté — liez un dossier à cette société.</p>
            <Button onClick={() => setShowPicker(true)} className="bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 font-bold h-9 text-xs" data-testid="open-od-picker">
              <Link2 className="w-4 h-4 mr-1" /> Lier un dossier OneDrive
            </Button>
          </div>
        )}
      </div>

      {showPicker && <OneDrivePicker api={api} onClose={() => setShowPicker(false)} onLinked={load} />}
    </div>
  );
};
